import { IdPathParameters, SaveOperation } from '@infuse/api/common';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

export const useGenericDemoteRole = ({
  apiFn,
  forcedParentId
}: {
  apiFn: SaveOperation<undefined, never> | SaveOperation<undefined, IdPathParameters<string>>;
  forcedParentId?: string;
}) => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<unknown, unknown, { parentId?: string; roleId: string }>({
    mutationFn: async ({ parentId, roleId }) => {
      if (forcedParentId) {
        parentId = forcedParentId;
      }

      try {
        await apiFn({ id: parentId, roleId: roleId }, undefined);

        notify({
          title: t('iam.roles.header.demote_role'),
          message: t('iam.roles.notify.demote_role_success')
        });
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.roles.header.demote_role'),
          message: getAxiosErrorMessage(e, t('iam.roles.notify.demote_role_failed'))
        });
        throw e;
      }
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
    }
  });

  return {
    ...mutation
  };
};
